/**
 * IndexRouter — what the user sees when they visit `/`.
 *
 * Anonymous visitors get the multi-page marketing site shipped by
 * Designer (`apps/web/public/marketing/`). We use a full-page
 * navigation via `window.location.replace` so the marketing HTML
 * is served pristine — no React Router shell, no Vite/SPA CSS
 * bleeding into the landing styles, no `<head>` collisions, and
 * relative links between marketing pages (`platform.html`,
 * `customers.html`, etc.) resolve correctly.
 *
 * Authenticated visitors go straight to the dashboard at `/home`
 * via React Router's `<Navigate>` (no full reload — fast bounce).
 *
 * The brief loading state covers the `/auth/me` probe in
 * AuthProvider so we don't flash marketing to a returning user.
 */
import { useEffect } from "react";
import { Loader2 } from "lucide-react";
import { Navigate } from "react-router";

import { useAuth } from "../AuthContext";

const LANDING_PATH = "/marketing/index.html";

export function IndexRouter() {
  const { status } = useAuth();

  // Trigger the hard navigation in an effect so we don't hit the
  // "side effect during render" warning React 18 logs in strict mode.
  useEffect(() => {
    if (status === "anonymous") {
      window.location.replace(LANDING_PATH);
    }
  }, [status]);

  if (status === "loading" || status === "anonymous") {
    return (
      <div className="flex min-h-screen items-center justify-center bg-slate-50">
        <Loader2 className="h-5 w-5 animate-spin text-slate-400" />
      </div>
    );
  }

  // status === "authenticated"
  return <Navigate to="/home" replace />;
}
